// src/pages/ViewRecord.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardHeader, CardContent, CardActions, Chip, Typography, Stack,
  Divider, IconButton, Button, Tooltip, Grid, Skeleton, Table, TableBody,
  TableCell, TableHead, TableRow, TableContainer, Paper, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams, useNavigate } from 'react-router-dom';

import { useI18n } from '../i18n/i18n';
import { getData, saveData } from '../services/storageService';
import ExportService from '../services/exportService';

// --- Словники для відображення назв (як у TabsWrapper) ---
const TUNNEL_PROGRAMS = {
  pro1: 'Pro.1 - sábanas',
  pro2: 'Pro.2 - sábana nueva', 
  pro3: 'Pro.3 - toalla nueva',
  pro4: 'Pro.4 - mantelería',
  pro6: 'Pro.6 - toallas espigas/rayas',
  pro8: 'Pro.8 - toallas color',
  pro9: 'Pro.9 - nórdicos',
  pro10: 'Pro.10 - premium',
  pro14: 'Pro.14 - albornoces',
  r11: 'Pro.11 - rechazo sáb',
  r12: 'Pro.12 - rechazo mant',
  r13: 'Pro.13 - rechazo toalla',
  rotas: 'Rotas - limpieza',
  total: 'Total'
};

const METER_LABELS = {
  aguaTunel: 'Cont. agua túnel',
  aguaPozo: 'Cont. agua pozo',
  aguaLav: 'Cont. agua lavadoras',
  aguaCal: 'Cont. agua calderas',
  propDep: 'Cont. propano depósito',
  propCaldera: 'Cont. propano caldera',
  propSaba: 'Cont. propano cal. saba',
  propMedio: 'Cont. propano cal. medio',
  propHibri: 'Cont. propano cal. hibri',
  propSec: 'Cont. propano secadoras'
};

const WASHER_CYCLE_LABELS = {
  mant: 'Mantelería',
  sab: 'Sábanas',
  pis: 'P piscina',
  otr: 'Otros',
  'otr-29': 'Otros - programa 29',
  'otr-30': 'Otros - programa 30',
  cent: 'Centrífugo'
};

const CLIENT_PROGRAMS = [
    { code: '01', name: 'Sábana' }, { code: '02', name: 'Sábana nueva' }, { code: '03', name: 'Toallas nuevas' },
    { code: '04', name: 'Manteleria' }, { code: '06', name: 'Toallas Espigas/Rayas' }, { code: '08', name: 'Toallas color' },
    { code: '09', name: 'Nórdicos' }, { code: '10', name: 'Toallas Premium' }, { code: '11', name: 'Rechazo sábana' },
    { code: '12', name: 'Rechazo manteleria' }, { code: '13', name: 'Rechazo toallas' }, { code: '14', name: 'Albornoces' },
    { code: 'otros', name: 'Otros' },
];

// Функція для обчислення циклів з символів "|"
const calculateCycles = (cyclesData) => {
  if (!cyclesData) return {};
  
  const result = {};
  Object.entries(cyclesData).forEach(([washer, cycles]) => {
    if (typeof cycles === 'string' && cycles.includes('|')) {
      result[washer] = cycles.split('|').length - 1;
    } else if (cycles && Number(cycles) > 0) {
      result[washer] = Number(cycles);
    }
  });
  
  return result;
};

// --- Componentes auxiliares ---

const KeyValueTable = ({ data, title, unit = '', isTunnel = false, keyMap = null }) => {
  const { t } = useI18n();
  
  const filteredData = Object.entries(data || {})
    .filter(([key, value]) => 
      (typeof value === 'string' && value.length > 0) || (typeof value === 'number' && Number(value) > 0)
    )
    .map(([key, value]) => {
      const label = isTunnel 
        ? (TUNNEL_PROGRAMS[key] || key) 
        : (keyMap ? (keyMap[key] || key) : key);
        
      return { key, label, value };
    })
    .sort((a, b) => {
      if (a.key === 'total') return 1;
      if (b.key === 'total') return -1;
      return a.key.localeCompare(b.key);
    });

  if (filteredData.length === 0) {
    return (
      <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('meter_analysis.no_data')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
          {title}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {isTunnel ? t('labels_program') : t('reports_meter_header_name')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {t('meter_analysis.value')} {unit && `(${unit})`}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map(({ key, label, value }) => (
                <TableRow key={key} hover>
                  <TableCell component="th" scope="row" sx={{ fontWeight: key === 'total' ? 'bold' : 'medium' }}>
                    {label}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: key === 'total' ? 'bold' : 'medium' }}>
                    {String(value).includes('|') 
                      ? <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '1.1rem', color: '#007bff', fontWeight: 'bold' }}>{value}</Typography>
                      : `${value} ${unit}`.trim()
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

const ClientWeightTable = ({ data }) => {
  const { t } = useI18n();
  
  const validData = (data || []).filter(row => row.client && row.weight);

  if (validData.length === 0) {
    return (
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
            {t('view_client_weights')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('meter_analysis.no_data')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
          {t('view_client_weights')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.default' }}>
                  {t('labels_client')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.default' }}>
                  {t('labels_program')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'background.default' }}>
                  {t('labels_kg')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {validData.map((row, index) => {
                const programObj = CLIENT_PROGRAMS.find(p => p.code === row.program);
                const programName = programObj ? programObj.name : (row.program || '—');

                return (
                  <TableRow key={index} hover>
                    <TableCell>{row.client}</TableCell>
                    <TableCell>{programName}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.weight}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

const WasherCyclesTable = ({ data }) => {
  const { t } = useI18n();
  
  const cyclesData = calculateCycles(data);
  const validData = Object.entries(cyclesData).filter(([_, cycles]) => cycles > 0);

  if (validData.length === 0) {
    return (
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          {/* ВИКОРИСТАННЯ КЛЮЧА */}
          <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
            {t('view_washer_cycles')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('meter_analysis.no_data')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <CardContent>
        {/* ВИКОРИСТАННЯ КЛЮЧА */}
        <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
          {t('view_washer_cycles')}
        </Typography>
        {/* ВИКОРИСТАННЯ КЛЮЧА */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('view_washer_cycles_note')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.default' }}>
                  {t('labels_machine')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'background.default' }}>
                  {t('labels_cycles')}
                </TableCell>
                {/* ВИКОРИСТАННЯ КЛЮЧА */}
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.default' }}>
                  {t('view_washer_cycles_original')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {validData.map(([washerKey, cycles]) => {
                const keyPrefix = washerKey.split('-')[0];
                const weight = washerKey.split('-')[1];
                const label = `${WASHER_CYCLE_LABELS[keyPrefix] || keyPrefix} ${weight ? `(${weight} kg)` : ''}`;

                return (
                  <TableRow key={washerKey} hover>
                    <TableCell>{label}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{cycles}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                        {data[washerKey]}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

const MetersTable = ({ data }) => {
  const { t } = useI18n();

  const validData = Object.entries(data || {}).filter(([_, value]) => 
    value !== null && value !== undefined && value !== '' && Number(value) > 0
  );

  if (validData.length === 0) {
    return (
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
            {t('view_meter_readings')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('meter_analysis.no_data')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
          {t('view_meter_readings')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.default' }}>
                  {t('reports_meter_header_name')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'background.default' }}>
                  {t('meter_analysis_value')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {validData.map(([meter, value]) => (
                <TableRow key={meter} hover>
                  <TableCell>{METER_LABELS[meter] || meter}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

const InfoChip = ({ label, value, color = "default" }) => (
  <Chip 
    label={`${label}: ${value}`} 
    color={color} 
    variant="outlined"
    sx={{ fontWeight: 'medium' }}
  />
);

// ---------- Componente Principal ----------
export default function ViewRecord() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editedRecord, setEditedRecord] = useState(null);

  const isAdmin = true;

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!routeId) {
        setError(t('view_record.errors.no_id'));
        setLoading(false);
        return;
      }
      
      const recordId = decodeURIComponent(String(routeId));
      const allRecords = await getData('lainsa_records') || [];
      const rec = allRecords.find(r => String(r.id) === recordId);
      
      if (rec) {
        setRecord(rec);
        setEditedRecord(JSON.parse(JSON.stringify(rec)));
      } else {
        setError(t('view_record.errors.not_found'));
        setRecord(null);
      }
    } catch (error) {
      console.error('🚨 Error cargando registro:', error);
      setError(t('view_record.errors.loading_error'));
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [routeId, t]);

  useEffect(() => { 
    reload(); 
  }, [reload]);

  const isShiftReport = record?.recordType === 'shift_report';

  const headerTitle = isShiftReport 
    ? `${t('view_shift_report_title')}: ${record ? new Date(record.fecha || record.date).toLocaleDateString() : ''}`
    : t('view_title');

  const exportToPDF = () => {
    if (!record) return;
    try {
      ExportService.exportReportToPDF(record);
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert(t('dashboard.pdf.generation_error'));
    }
  };

  const downloadPDF = () => {
    if (!record) return;
    try {
      ExportService.downloadReportPDF(record);
    } catch (error) {
      console.error('Error descargando PDF:', error);
      alert(t('dashboard.pdf.generation_error'));
    }
  };

  const exportToExcel = () => {
    if (!record) return;
    try {
      ExportService.exportReportToExcel(record);
    } catch (error) {
      console.error('Error generando Excel:', error);
      alert(t('archive.excel_error'));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const allRecords = await getData('lainsa_records') || [];
      const updatedRecords = allRecords.map(r => 
        r.id === editedRecord.id ? editedRecord : r
      );
      
      await saveData('lainsa_records', updatedRecords);
      setRecord(editedRecord);
      setEditDialogOpen(false);
      alert(t('edit_record_success'));
    } catch (error) {
      console.error('Error guardando cambios:', error);
      alert(t('edit_record_error'));
    }
  };

  const handleDelete = async () => {
    try {
      const allRecords = await getData('lainsa_records') || [];
      const updatedRecords = allRecords.filter(r => r.id !== record.id);
      
      await saveData('lainsa_records', updatedRecords);
      setDeleteDialogOpen(false);
      alert(t('delete_record_success'));
      navigate(-1);
    } catch (error) {
      console.error('Error eliminando registro:', error);
      alert(t('delete_record_error'));
    }
  };

  const handleFieldChange = (section, field, value) => {
    const numericValue = isNaN(Number(value)) ? value : Number(value);
    
    setEditedRecord(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: numericValue
      }
    }));
  };

  return (
    <Box sx={{ p: 3, '@media print': { p: 0 } }}>
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', '@media print': { boxShadow: 'none', borderRadius: 0 } }}>
        <CardHeader
          title={
            <Typography variant="h4" fontWeight="bold">
              {headerTitle}
            </Typography>
          }
          subheader={
            record && (
              <Typography variant="subtitle1" color="text.secondary">
                ID: {record.id} • {t('common.type')}: {record.recordType || 'registro'}
              </Typography>
            )
          }
          action={
            <Stack direction="row" spacing={1}>
              {isAdmin && (
                <>
                  <Tooltip title={t('staff_edit')}>
                    <IconButton 
                      onClick={handleEdit}
                      size="large"
                      sx={{ 
                        backgroundColor: 'warning.main', 
                        color: 'white',
                        '&:hover': { backgroundColor: 'warning.dark' },
                        '@media print': { display: 'none' }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('actions_remove')}>
                    <IconButton 
                      onClick={() => setDeleteDialogOpen(true)}
                      size="large"
                      sx={{ 
                        backgroundColor: 'error.main', 
                        color: 'white',
                        '&:hover': { backgroundColor: 'error.dark' },
                        '@media print': { display: 'none' }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title={t('common_back')}>
                <IconButton 
                  onClick={() => navigate(-1)} 
                  size="large"
                  sx={{ 
                    backgroundColor: 'primary.main', 
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                    '@media print': { display: 'none' }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          }
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        />

        <CardContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading && (
            <Box>
              <Skeleton height={40} width="60%" sx={{ mb: 2 }} />
              <Skeleton height={20} width="40%" sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                {[...Array(4)].map((_, i) => (
                  <Grid item xs={12} key={i}>
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {!loading && !record && !error && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h5" color="error" gutterBottom>
                {t('view_record.errors.not_found_generic')}
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIcon />}
              >
                {t('common_back')}
              </Button>
            </Box>
          )}

          {!loading && record && (
            <Box>
              {isShiftReport ? (
                // --- КОМПОНУВАННЯ В ОДИН СТОВПЕЦЬ ---
                <Grid container spacing={3}>
                  
                  {/* 1. Resumen del Turno (Шапка) */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: 'background.default' }}>
                      <Typography variant="h5" gutterBottom fontWeight="bold">
                        {t('view_shift_summary')}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
                        <InfoChip label={t('labels_date')} value={record.fecha || record.date} color="primary" />
                        <InfoChip label={t('labels_shift')} value={record.turno || record.shift} color="secondary" />
                        <InfoChip 
                          label={t('labels_operators')} 
                          value={(record.operators || []).join(', ') || t('common.not_specified')} 
                          color="success" 
                        />
                        {record.closedAt && (
                          <InfoChip 
                            label={t('view_record.closed_at')} 
                            value={new Date(record.closedAt).toLocaleString()} 
                            color="info" 
                          />
                        )}
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* 2. Producción Túnel */}
                  <Grid item xs={12}>
                    <KeyValueTable 
                      data={record.tunel} 
                      title={t('view_record.sections.tunnel_production')} 
                      unit="kg"
                      isTunnel={true}
                    />
                  </Grid>
                  
                  {/* 3. Rechazo del Túnel */}
                  <Grid item xs={12}>
                    <KeyValueTable 
                      data={record.rech_tunel} 
                      title={t('view_tunnel_rejection')} 
                      unit="kg"
                      isTunnel={true}
                    />
                  </Grid>
                  
                  {/* 4. Peso por Cliente */}
                  <Grid item xs={12}>
                    <ClientWeightTable data={record.pesoCliente} />
                  </Grid>

                  {/* 5. Ciclos de Lavadoras */}
                  <Grid item xs={12}>
                    <WasherCyclesTable data={record.lav_ciclos} />
                  </Grid>
                  
                  {/* 6. Lecturas de Contadores */}
                  <Grid item xs={12}>
                    <MetersTable data={record.cont} />
                  </Grid>

                  {/* Спостереження (якщо є) */}
                  {record.observaciones && (
                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
                            {t('view_record.sections.observations')}
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {record.observaciones}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* 7. Información Técnica (Знизу) */}
                  <Grid item xs={12}>
                    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          {t('view_record.sections.technical_info')}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                          <InfoChip label="ID" value={record.id} color="default" />
                          <InfoChip label={t('common.type')} value={record.recordType} color="default" />
                          {record.createdAt && (
                            <InfoChip 
                              label={t('view_record.created_at')} 
                              value={new Date(record.createdAt).toLocaleString()} 
                              color="default" 
                            />
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                </Grid>
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('view.standard_record')}
                  </Typography>
                  <Typography color="text.secondary">
                    Це стандартний запис (не звіт про зміну)
                  </Typography>
                  <Box sx={{ mt: 2, p: 2, textAlign: 'left', bgcolor: 'grey.50', borderRadius: 1, overflowX: 'auto' }}>
                    <Typography variant="body2" component="pre">
                      {JSON.stringify(record, null, 2)}
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Box>
          )}
        </CardContent>

        {record && (
          <CardActions sx={{ 
            justifyContent: 'space-between', 
            p: 3, 
            borderTop: '1px solid', 
            borderColor: 'divider',
            '@media print': { display: 'none' } 
          }}>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate(-1)}
              variant="outlined"
            >
              {t('common_back')}
            </Button>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              <Button 
                startIcon={<PrintIcon />} 
                onClick={handlePrint}
                variant="outlined"
              >
                {t('common.print')}
              </Button>
              <Button 
                startIcon={<PictureAsPdfIcon />} 
                onClick={downloadPDF}
                variant="contained"
                color="info"
              >
                {t('actions_download_pdf')} 
              </Button>
              <Button 
                startIcon={<PictureAsPdfIcon />} 
                onClick={exportToPDF}
                variant="contained"
                color="secondary"
              >
                {t('actions_view_pdf')}
              </Button>
              <Button 
                startIcon={<DownloadIcon />} 
                onClick={exportToExcel}
                variant="contained"
                color="success"
              >
                {t('common.export_excel')}
              </Button>
            </Stack>
          </CardActions>
        )}
      </Card>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('staff_edit')}</DialogTitle>
        <DialogContent>
          {editedRecord && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>{t('view_record.sections.tunnel_production')}</Typography>
              <Grid container spacing={2}>
                {Object.keys(editedRecord.tunel || {}).map((key) => (
                  key !== 'total' && (
                    <Grid item xs={6} sm={4} key={key}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={TUNNEL_PROGRAMS[key] || key}
                        value={editedRecord.tunel[key] || ''}
                        onChange={(e) => handleFieldChange('tunel', key, e.target.value)}
                      />
                    </Grid>
                  )
                ))}
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>{t('view_tunnel_rejection')}</Typography>
              <Grid container spacing={2}>
                {Object.keys(editedRecord.rech_tunel || {}).map((key) => (
                  key !== 'total' && (
                    <Grid item xs={6} sm={4} key={key}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={TUNNEL_PROGRAMS[key] || key}
                        value={editedRecord.rech_tunel[key] || ''}
                        onChange={(e) => handleFieldChange('rech_tunel', key, e.target.value)}
                      />
                    </Grid>
                  )
                ))}
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>{t('view_record.sections.observations')}</Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={editedRecord.observaciones || ''}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, observaciones: e.target.value }))}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common_cancel')}</Button>
          <Button onClick={handleSaveEdit} variant="contained">{t('common_save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('actions_remove')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirm_delete_record')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common_cancel')}</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            {t('actions_remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


